'use client';

import { Store } from 'lucide-react';
import { useState } from 'react';
import { TotemFlyToCart } from '@/app/(users)/totem-by-slug/[slug]/components/animations/totem-fly-to-cart';
import { TotemCheckoutDialog } from '@/app/(users)/totem-by-slug/[slug]/components/checkout/totem-checkout-dialog';
import { TotemShell } from '@/app/(users)/totem-by-slug/[slug]/components/layout/totem-shell';
import {
  MultilocalLocation,
  MultilocalLocationSelector,
} from '@/app/(users)/totem-by-slug/[slug]/components/multilocal-location-selector';
import { TotemProductSheet } from '@/app/(users)/totem-by-slug/[slug]/components/product/totem-product-sheet';
import { TotemEmptyState } from '@/app/(users)/totem-by-slug/[slug]/components/totem-empty-state';
import { TotemProvider, useTotem } from '@/app/(users)/totem-by-slug/[slug]/providers/totem-provider';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

function TotemViewContent() {
  const { totem, totemLoading, menuLoading, menu } = useTotem();
  const [selectedMultilocalStore, setSelectedMultilocalStore] = useState<MultilocalLocation | null>(null);

  if (totemLoading || menuLoading) {
    return (
      <div className="flex h-full w-full flex-col">
        <Skeleton className="h-24 w-full rounded-none" />
        <Skeleton className="min-h-0 flex-1 rounded-none" />
        <Skeleton className="h-20 w-full rounded-none" />
      </div>
    );
  }

  if (!totem) {
    return (
      <TotemEmptyState description="El tótem solicitado no existe o no está disponible." title="Tótem no encontrado" />
    );
  }

  // Si el tótem pertenece a un grupo Multilocal y el cliente aún no ha seleccionado una tienda
  const multilocalGroup = totem.multilocal_groups;
  const rawLocations = multilocalGroup?.multilocal_group_locations || [];
  const multilocalLocations: MultilocalLocation[] = rawLocations
    .filter((item) => item.is_visible !== false)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map((item) => {
      if (!item.locations) return null;
      return {
        ...item.locations,
        square_image: item.square_image,
        banner_image: item.banner_image,
      };
    })
    .filter(Boolean) as MultilocalLocation[];

  if (multilocalGroup && multilocalLocations.length > 0 && !selectedMultilocalStore) {
    const layout = multilocalGroup.mosaic_layout === 'vidriera' ? 'twoColumn' : 'threeColumn';
    return (
      <MultilocalLocationSelector
        groupName={multilocalGroup.name}
        layout={layout}
        locations={multilocalLocations}
        onSelectLocation={(loc) => setSelectedMultilocalStore(loc)}
      />
    );
  }

  if (!totem.active_menu_id || !menu) {
    return <TotemEmptyState description="Este tótem no tiene un menú activo configurado." title="Menú no disponible" />;
  }

  return (
    <div className={cn('flex h-full w-full flex-col relative')}>
      {multilocalGroup && selectedMultilocalStore && (
        <div className="bg-zinc-900 border-b border-zinc-800 px-4 py-2 flex items-center justify-between text-xs text-zinc-300">
          <span className="flex items-center gap-2">
            <Store className="size-4 text-orange-500" />
            <span>
              Patio: <strong className="text-white">{multilocalGroup.name}</strong> • Local:{' '}
              <strong className="text-orange-400">{selectedMultilocalStore.name}</strong>
            </span>
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-orange-400 hover:text-orange-300 hover:bg-zinc-800"
            onClick={() => setSelectedMultilocalStore(null)}
          >
            ← Cambiar local
          </Button>
        </div>
      )}
      <TotemShell />
      <TotemProductSheet />
      <TotemCheckoutDialog />
      <TotemFlyToCart />
    </div>
  );
}

export function TotemView({ totemId }: { totemId: string }) {
  return (
    <TotemProvider totemId={totemId}>
      <TotemViewContent />
    </TotemProvider>
  );
}
